import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import DirectionsIcon from "@mui/icons-material/Directions";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import CircularProgress from "@mui/material/CircularProgress";
import { Navigate } from "react-router-dom";

import Navbar from "../../components/Navbar";
import zipzyAdminApiClient from "../../apiClients/zipzyAdminApiClient";

const AUTO_REFRESH_INTERVAL_MS = 30_000;

function timeSince(isoDateString) {
  const date = new Date(isoDateString);
  const now = new Date();
  const diffMs = now - date;

  if (isNaN(date.getTime())) {
    return "Invalid date";
  }

  // Convert ms to time components
  const totalSeconds = Math.floor(diffMs / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  const seconds = totalSeconds % 60;
  const minutes = totalMinutes % 60;
  const hours = totalHours % 24;

  // Less than 1 minute → seconds
  if (totalMinutes < 1) {
    return `${seconds}s`;
  }

  // Has days
  if (totalDays > 0) {
    return `${totalDays}d ${hours}h ${minutes}m`;
  }

  // No days, but has hours
  if (totalHours > 0) {
    return `${hours}h ${minutes}m`;
  }

  // Only minutes
  return `${minutes}m`;
}

function renderCustomerStr(customerStr) {
  if (customerStr.length === 0) {
    return null;
  }

  return (
    <span>
      {" "}
      with <strong>{customerStr}</strong>{" "}
    </span>
  );
}

function getDateStr({ driverAnalyticsObject }) {
  const { status, currentRide, updatedAt } = driverAnalyticsObject;

  const formatStatus = (status) => {
    if (status === "UNAVAILABLE_HEADING_BACK_TO_LOCALITY") {
      return "Heading Back";
    }

    const newStatus = status
      .split("_")
      .map((w) => w[0] + w.slice(1).toLowerCase())
      .join(" ");
    return newStatus;
  };

  let sinceStr = "";
  if (status === "IDLE" || status === "MANUAL_OFFLINE" || status === "UNAVAILABLE_HEADING_BACK_TO_LOCALITY") {
    sinceStr = `${timeSince(updatedAt)}`;
  } else {
    sinceStr = `${timeSince(currentRide.createdAt)}`;
  }

  const formattedStatus = formatStatus(status);

  let customerStr = "";
  if (status === "IN_RIDE" && currentRide && currentRide.customer) {
    customerStr = `${currentRide.customer.fullName}`;
  }

  return (
    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }} textAlign="left">
      {formattedStatus}
      {renderCustomerStr(customerStr)} since <strong>{sinceStr}</strong>.
    </Typography>
  );
}

function formatTimeFromMilliseconds(ms) {
  const totalSeconds = Math.floor(ms / 1000);

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  // > 1 day  → show d + h
  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  // > 1 hour → show h + m
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  // else → show m + s
  return `${minutes}m ${seconds}s`;
}

function isLocationOutdated(currentLocation) {
  if (currentLocation === null || currentLocation === undefined) {
    return {
      isOutdated: true,
      timeDifferenceStr: "N/A",
    };
  }

  const currentUtcTimestamp = Date.now();
  const timeDifference = currentUtcTimestamp - currentLocation.updatedAt;
  const isOutdated = timeDifference > 5 * 60 * 1000;

  const timeDifferenceStr = formatTimeFromMilliseconds(timeDifference);

  return {
    isOutdated: isOutdated,
    timeDifferenceStr: timeDifferenceStr,
  };
}

function LocationDisplay({ currentLocation }) {
  const isLocationOutdatedResult = isLocationOutdated(currentLocation);

  const locationStatus =
    currentLocation === null || currentLocation === undefined
      ? "UNAVAILABLE"
      : isLocationOutdatedResult.isOutdated
      ? "OUTDATED"
      : "AVAILABLE";

  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        width: "100%",
        mt: 1,
        pt: 1,
        borderTop: "1px dashed #999999",
      }}
    >
      {locationStatus === "UNAVAILABLE" && <Chip label="Location Unavailable" color="error" variant="outlined" />}

      {locationStatus === "OUTDATED" && <Chip label="Location Outdated" color="warning" variant="outlined" />}

      {locationStatus === "AVAILABLE" && <Chip label="Location Available" color="success" variant="outlined" />}

      {(locationStatus === "OUTDATED" || locationStatus === "AVAILABLE") && (
        <Typography variant="body2" color="textSecondary" sx={{}}>
          <strong>{isLocationOutdatedResult.timeDifferenceStr}</strong> ago
        </Typography>
      )}

      {(locationStatus === "OUTDATED" || locationStatus === "AVAILABLE") && (
        <IconButton
          color="primary"
          variant="contained"
          href={`https://www.google.com/maps?q=${currentLocation.latitude},${currentLocation.longitude}`}
          target="_blank"
          rel="noopener"
          sx={{
            bgcolor: "#cecdcdff",
          }}
        >
          <DirectionsIcon />
        </IconButton>
      )}
    </Box>
  );
}

function DriverAnalyticsItem({ driverAnalyticsObject }) {
  const { fullName, phoneNumber, locality, status, currentRide } = driverAnalyticsObject;

  // Extract first name only
  const firstName = fullName.split(" ")[0];

  // Format status text like "In Ride"
  const formatStatus = (status) => {
    if (status === "UNAVAILABLE_HEADING_BACK_TO_LOCALITY") {
      return "Heading Back";
    }

    const newStatus = status
      .split("_")
      .map((w) => w[0] + w.slice(1).toLowerCase())
      .join(" ");
    return newStatus;
  };

  // Color coding
  const statusColor =
    status === "IDLE"
      ? "success"
      : status === "IN_RIDE"
      ? "warning"
      : status === "UNAVAILABLE_HEADING_BACK_TO_LOCALITY"
      ? "secondary"
      : "error";

  // Format date: "hh:mm AM/PM, DD MMM YYYY"
  const formatDate = (iso) => {
    const date = new Date(iso);
    return date.toLocaleString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        width: "90%",
        maxWidth: 600,
        mb: 2,
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {firstName}
          </Typography>

          <Chip label={formatStatus(status)} color={statusColor} variant="filled" sx={{ width: "fit-content" }} />
        </Box>

        {getDateStr({ driverAnalyticsObject })}

        <LocationDisplay currentLocation={driverAnalyticsObject.currentLocation} />
      </CardContent>
    </Card>
  );
}

export default function DriversAnalyticsPage() {
  const isLoggedIn = zipzyAdminApiClient.isLoggedIn();

  const [isLoading, setIsLoading] = useState(true);
  const [driversAnalytics, setDriversAnalytics] = useState([]);

  const fetchDriverAnalyticsFromApi = async () => {
    setIsLoading(true);
    const response = await zipzyAdminApiClient.getDriverAnalytics();
    if (!response.success) {
      window.alert(response.error.userFriendlyMessage);
      setIsLoading(false);
      return;
    }

    setDriversAnalytics(response.data.driversStatuses);
    setIsLoading(false);
  };

  useEffect(() => {
    document.title = "Driver Analytics - Zipzy Admin";

    if (!isLoggedIn) {
      return;
    }

    fetchDriverAnalyticsFromApi();

    const intervalId = setInterval(() => {
      fetchDriverAnalyticsFromApi();
    }, AUTO_REFRESH_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  if (!isLoggedIn) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="center"
      height="100vh"
      textAlign="center"
    >
      <Navbar />

      <Box
        sx={{
          mb: 4,
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          width: "90%",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
          }}
        >
          Realtime Driver Status
        </Typography>

        {isLoading && (
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && (
          <IconButton
            onClick={() => fetchDriverAnalyticsFromApi()}
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        )}
      </Box>

      {driversAnalytics.map((driverAnalyticsObject) => (
        <DriverAnalyticsItem key={driverAnalyticsObject._id} driverAnalyticsObject={driverAnalyticsObject} />
      ))}
    </Box>
  );
}
